import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { queryKeys } from '@/lib/query-keys';

/**
 * M2 (#572): cross-workspace email monitoring for a single enterprise partner.
 *
 * Wraps the three partner-scoped, SECURITY-DEFINER read RPCs that enumerate
 * every campaign / sender-account / quota across the workspaces the partner
 * provisioned (`provisioned_by_partner_id`). Each RPC re-computes authorization
 * server-side (`caller_has_partner_capability(partner_id,'email_monitor')`) and
 * filters every row to the partner's workspaces — so this hook is a thin data
 * layer, not a security boundary.
 *
 * Polls every 30s so the board reflects in-flight campaigns and sender drift.
 *
 * The hook keeps its collapsed `{ campaigns, senders, quotas, isLoading, error,
 * refetch }` shape (the M2 page + its test depend on it) AND additively exposes
 * the three underlying `useQuery` objects (`campaignsQuery`, `sendersQuery`,
 * `quotasQuery`). The M3 dashboard reuses THESE same queries so each alert/email
 * card gets its own skeleton + error + per-card retry without re-calling the 3
 * M2 RPCs (the new M3 hooks are only for the 2 new M3 RPCs).
 */

/** One campaign across a partner's workspaces (partner_campaign_overview row). */
export interface PartnerCampaignOverviewRow {
  campaign_id: string;
  workspace_id: string;
  workspace_name: string;
  campaign_name: string;
  status: string;
  recipient_count: number;
  emails_sent: number;
  emails_failed: number;
  emails_pending: number;
  /** Displayed failure rate from campaign counters (0..1). */
  failure_rate: number;
  /** Displayed unsubscribe rate from the unsubscribe log (0..1). */
  unsubscribe_rate: number;
  /** A real (non-202/400) send-log error occurred in the window. */
  has_real_error: boolean;
  /** Composite flag: real error in window OR failure_rate > threshold. */
  needs_attention: boolean;
}

/** One sender account's health (partner_sender_health row). */
export interface PartnerSenderHealthRow {
  workspace_id: string;
  workspace_name: string;
  email_address: string;
  provider: string;
  emergency_pause_active: boolean;
  consecutive_token_refresh_failures: number;
  expires_at: string | null;
  /** Outlook account connected via SPA flow → subject to the 24h refresh cap. */
  outlook_spa_24h_risk: boolean;
  health_status: 'connected' | 'needs_reconnect' | 'expired';
}

/** One account's quota consumption today (partner_member_quota row). */
export interface PartnerMemberQuotaRow {
  workspace_id: string;
  workspace_name: string;
  email_address: string;
  emails_sent_today: number;
  daily_limit: number;
  /** Fraction of the daily limit consumed today (0..1). */
  pct_consumed: number;
  /** At or above the near-wall fraction of the daily limit. */
  near_wall: boolean;
}

const REFETCH_INTERVAL_MS = 30_000;

export function useEnterpriseEmailMonitoring(partnerId: string) {
  const enabled = !!partnerId;

  const campaignsQuery = useQuery({
    queryKey: queryKeys.enterprisePartners.monitoringCampaigns(partnerId),
    queryFn: async () => {
      const { data, error } = await supabase.rpc('partner_campaign_overview', {
        p_partner_id: partnerId,
      });
      if (error) throw error;
      return (data ?? []) as PartnerCampaignOverviewRow[];
    },
    enabled,
    refetchInterval: REFETCH_INTERVAL_MS,
  });

  const sendersQuery = useQuery({
    queryKey: queryKeys.enterprisePartners.senderHealth(partnerId),
    queryFn: async () => {
      const { data, error } = await supabase.rpc('partner_sender_health', {
        p_partner_id: partnerId,
      });
      if (error) throw error;
      return (data ?? []) as PartnerSenderHealthRow[];
    },
    enabled,
    refetchInterval: REFETCH_INTERVAL_MS,
  });

  const quotasQuery = useQuery({
    queryKey: queryKeys.enterprisePartners.memberQuota(partnerId),
    queryFn: async () => {
      const { data, error } = await supabase.rpc('partner_member_quota', {
        p_partner_id: partnerId,
      });
      if (error) throw error;
      return (data ?? []) as PartnerMemberQuotaRow[];
    },
    enabled,
    refetchInterval: REFETCH_INTERVAL_MS,
  });

  const refetch = () => {
    void campaignsQuery.refetch();
    void sendersQuery.refetch();
    void quotasQuery.refetch();
  };

  return {
    campaigns: campaignsQuery.data ?? [],
    senders: sendersQuery.data ?? [],
    quotas: quotasQuery.data ?? [],
    isLoading:
      campaignsQuery.isLoading || sendersQuery.isLoading || quotasQuery.isLoading,
    /** First error across the three queries, for telemetry/UI. */
    error: campaignsQuery.error ?? sendersQuery.error ?? quotasQuery.error ?? null,
    refetch,
    // Additive: the underlying per-RPC query objects, so a consumer can drive
    // independent per-card skeleton + error + retry off the SAME queries.
    campaignsQuery,
    sendersQuery,
    quotasQuery,
  };
}
